import {
  getData,
  updateData,
  createWithKey,
  removeData,
  
} from './api-wrapper.service';
import logger from '../../utils/logger';

// Types
export interface Injury {
  id: string;
  title: string;
  dateStarted: number; // When injury occurred (timestamp)
  dateEnded: number; // When recovered (0 if not recovered)
  dateCreated: number; // When record was created
  isRecovered: boolean;
  note?: string; // Optional notes
  photo?: { // Legacy single photo (object with url and aspectRatio)
    url: string;
    aspectRatio?: number;
  };
  photos?: Record<string, {  // Original app format: { "0": {...}, "1": {...} }
    url: string;
    aspectRatio: number;
    dateAdded?: number;
    caption?: string;
  }>;
  timezoneOffset?: number;
  owner?: string; // Not in original but needed for Phoenix
}

export interface InjuryInput {
  title: string;
  dateStarted: number;
  dateEnded?: number;
  isRecovered?: boolean;
  note?: string;
  owner: string;
}

/**
 * Get all injuries for a user
 */
export const getUserInjuries = async (userId: string): Promise<Injury[]> => {
  try {
    const data = await getData(`/injuries/${userId}`);
    
    if (!data) {
      return [];
    }
    
    // Convert object to array
    const injuries: Injury[] = Object.entries(data).map(([id, injury]: [string, any]) => ({
      id,
      ...injury,
      owner: userId
    }));
    
    // Sort by injury date, most recent first
    return injuries.sort((a, b) => b.dateStarted - a.dateStarted);
  } catch (error) {
    logger.error('Error fetching user injuries:', error);
    throw error;
  }
};

/**
 * Get a single injury by ID
 */
export const getInjuryById = async (userId: string, injuryId: string): Promise<Injury | null> => {
  try {
    const data = await getData(`/injuries/${userId}/${injuryId}`);
    
    if (!data) {
      return null;
    }
    
    return {
      id: injuryId,
      ...data,
      owner: userId
    };
  } catch (error) {
    logger.error(`Error fetching injury ${injuryId}:`, error);
    throw error;
  }
};

/**
 * Add a new injury
 */
export const addInjury = async (injuryData: InjuryInput): Promise<Injury> => {
  try {
    const userId = injuryData.owner;
    
    // Prepare data matching original app structure
    const dataToSave = {
      title: injuryData.title,
      dateStarted: injuryData.dateStarted,
      dateEnded: injuryData.dateEnded || 0,
      dateCreated: Date.now(),
      isRecovered: injuryData.isRecovered || false,
      note: injuryData.note || '',
      timezoneOffset: new Date().getTimezoneOffset() / 60
    };
    
    // Create injury
    const id = await createWithKey(`/injuries/${userId}`, dataToSave);
    
    return {
      id,
      ...dataToSave,
      owner: userId
    };
  } catch (error) {
    logger.error('Error adding injury:', error);
    throw error;
  }
};

export interface InjuredInterval {
  start: number;
  end: number;
  injuryId: string;
}

/**
 * Generate merged injury intervals matching original helper logic
 */
export const buildInjuredIntervals = (injuries: Record<string, Injury>): InjuredInterval[] => {
  const entries = Object.entries(injuries).map(([id, injury]) => ({
    injuryId: id,
    start: normalizeStart(injury.dateStarted),
    end: normalizeEnd(injury.dateEnded || Date.now())
  })).filter(entry => entry.start <= entry.end);

  if (entries.length === 0) {
    return [];
  }

  // Sort by start asc, then end asc
  entries.sort((a, b) => a.start - b.start || a.end - b.end);

  const merged: InjuredInterval[] = [];
  let current = { ...entries[0] };

  for (let i = 1; i < entries.length; i += 1) {
    const next = entries[i];
    if (next.start <= current.end) {
      // Overlapping or contiguous - extend current interval
      if (next.end > current.end) {
        current.end = next.end;
        current.injuryId = next.injuryId;
      }
    } else {
      merged.push({ ...current });
      current = { ...next };
    }
  }

  merged.push({ ...current });
  return merged;
};

const normalizeStart = (timestamp: number): number => {
  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

const normalizeEnd = (timestamp: number): number => {
  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  return date.getTime() + 86400000 - 1; // end-of-day
};

/**
 * Update an existing injury
 */
export const updateInjury = async (
  userId: string, 
  injuryId: string, 
  updates: Partial<InjuryInput>
): Promise<Injury> => {
  try {
    // Prepare update data matching original app structure
    const dataToUpdate: any = {};
    
    if (updates.title !== undefined) dataToUpdate.title = updates.title;
    if (updates.dateStarted !== undefined) dataToUpdate.dateStarted = updates.dateStarted;
    if (updates.dateEnded !== undefined) dataToUpdate.dateEnded = updates.dateEnded;
    if (updates.isRecovered !== undefined) {
      dataToUpdate.isRecovered = updates.isRecovered;
      // If marking as recovered and no dateEnded provided, use current time
      if (updates.isRecovered && !updates.dateEnded) {
        dataToUpdate.dateEnded = Date.now();
      }
    }
    if (updates.note !== undefined) dataToUpdate.note = updates.note;
    
    await updateData(`/injuries/${userId}/${injuryId}`, dataToUpdate);
    
    // Fetch and return updated injury
    const updated = await getInjuryById(userId, injuryId);
    if (!updated) {
      throw new Error('Failed to fetch updated injury');
    }
    
    return updated;
  } catch (error) {
    logger.error(`Error updating injury ${injuryId}:`, error);
    throw error;
  }
};

/**
 * Delete an injury
 */
export const deleteInjury = async (userId: string, injuryId: string): Promise<void> => {
  try {
    await removeData(`/injuries/${userId}/${injuryId}`);
  } catch (error) {
    logger.error(`Error deleting injury ${injuryId}:`, error);
    throw error;
  }
};

/**
 * Get active injuries (not recovered)
 */
export const getActiveInjuries = async (userId: string): Promise<Injury[]> => {
  try {
    const allInjuries = await getUserInjuries(userId);
    return allInjuries.filter(injury => !injury.isRecovered);
  } catch (error) {
    logger.error('Error fetching active injuries:', error);
    throw error;
  }
};

/**
 * Get recovered injuries
 */
export const getRecoveredInjuries = async (userId: string): Promise<Injury[]> => {
  try {
    const allInjuries = await getUserInjuries(userId);
    return allInjuries.filter(injury => injury.isRecovered);
  } catch (error) {
    logger.error('Error fetching recovered injuries:', error);
    throw error;
  }
};
